import { Box, Typography, useTheme } from "@mui/material";
import type { DeltaStatic, Sources } from "quill";
import { forwardRef, useImperativeHandle, useRef } from "react";
import ReactQuill, { type ReactQuillProps } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { extractStringFromHtml } from "utilz/helpers";
import DefaultToolbar from "./quill-editor-with-chip/DefaultToolbarForQuil";

interface QuillEditorV3Props extends Omit<ReactQuillProps, "onChange"> {
  name?: string;
  onChange: (
    { field, value }: { field: string; value: string },
    meta?: {
      value: string;
      delta: DeltaStatic;
      source: Sources;
      editor: ReactQuill.UnprivilegedEditor;
    },
  ) => void;
  isRequired?: boolean;
  error?: boolean;
  errorMessage?: string;
  showToolbar?: boolean;
  hideItemsInToolbar?: ("left_align" | "center_align" | "right_align")[];
  borderLessDesign?: boolean;
  maxHeight?: string;
  minHeight?: string;
  height?: string;
}

export const QuillEditorV3 = forwardRef<ReactQuill, QuillEditorV3Props>(
  (
    {
      onChange,
      onBlur,
      showToolbar = false,
      hideItemsInToolbar,
      borderLessDesign = false,
      ...props
    },
    customRef,
  ) => {
    const theme = useTheme();
    const editorRef = useRef<ReactQuill>(null);

    // Allow external refs while keeping our internal one for the toolbar.
    useImperativeHandle(
      customRef,
      () => editorRef.current as ReactQuill,
      [],
    );

    const editorHeight = props.height || "100%";

    const onChangeHandler = (
      value: string,
      delta: DeltaStatic,
      source: Sources,
      editor: ReactQuill.UnprivilegedEditor,
    ) => {
      onChange(
        {
          field: props.name || "",
          value: extractStringFromHtml(value).length > 0 ? value : "",
        },
        { value, delta, source, editor },
      );
    };

    return (
      <Box
        sx={{
          width: "100%",
          height: editorHeight,
          minHeight: props.minHeight || "300px",
          maxHeight: props.maxHeight || "100%",
          display: "flex",
          flexDirection: "column",
          border: borderLessDesign
            ? "none"
            : `1px solid ${theme.palette.neutral["010"]}`,
          borderRadius: borderLessDesign ? 0 : "8px",
          overflow: "hidden",
        }}
      >
        {showToolbar && (
          <Box
            sx={{
              borderBottom: `1px solid ${theme.palette.neutral["010"]}`,
              "& > *": { border: "none !important", borderRadius: "0 !important" },
            }}
          >
            <DefaultToolbar
              editorRef={editorRef}
              borderLessDesign
              hideItemsInToolbar={hideItemsInToolbar}
            />
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            "& .ql-container": {
              flex: 1,
              display: "flex",
              flexDirection: "column",
              border: "none !important",
              backgroundColor: "#FFF",
              overflow: "hidden",
            },
            "& .ql-editor": {
              flex: 1,
              overflowY: "auto",
              minHeight: "100px",
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
              border: props.error ? "1px solid #EF4C54" : "none",
              backgroundColor: "#F6F6F5",
              borderRadius: 0,
              padding: "12px",
            },
            "& .ql-editor > p": {
              color: "#767B85",
            },
            "& .ql-editor.ql-blank::before": {
              paddingLeft: "16px",
            },
          }}
        >
          <ReactQuill
            ref={editorRef}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
            theme="snow"
            value={props.value}
            onChange={onChangeHandler}
            onBlur={onBlur}
            placeholder={`${props.placeholder || ""}${
              props.isRequired ? " *" : ""
            }`}
            modules={{
              // DefaultToolbar drives formatting via editorRef — disable Quill's built-in toolbar.
              toolbar: false,
            }}
            {...props}
          />
        </Box>

        {props.error && (
          <Typography pl="16px" variant="body-small" color={"error"}>
            {props.errorMessage || "This is required field"}
          </Typography>
        )}
      </Box>
    );
  },
);

QuillEditorV3.displayName = "QuillEditorV3";
